import { isoDate, parseDate } from "./dates.js";

/**
 * A scenario is a forecast frozen at a moment, plus what you changed about it.
 *
 * Frozen deliberately. A link sent to an investor on Tuesday should still show
 * what was sent on Tuesday — if it tracked live data it would quietly become a
 * different argument, and the commentary written against it would stop being
 * true. So the transactions are copied at the point of creation and never
 * re-derived.
 *
 * Adjustments sit on top as a list rather than being baked into the copy, so
 * what was changed stays legible next to what was there — which is the whole
 * point of showing someone a scenario rather than a number.
 */

/** The parts of a transaction a frozen forecast actually needs. */
export function freezeTxn(t) {
  const s = t.source || {};
  return {
    key: t.key,
    date: isoDate(t.date),
    amount: t.amount,
    label: t.label,
    category: t.category,
    isPipeline: !!t.isPipeline,
    isAwaitingPo: !!t.isAwaitingPo,
    isReceived: !!t.isReceived,
    isHistory: !!t.isHistory,
    isManual: !!t.isManual,
    committed: !!t.committed,
    net: t.net ?? null,
    source: {
      type: s.type || null,
      id: s.id != null ? String(s.id) : null,
      docNo: s.docNo || null,
      contact: s.contact || null,
      stage: s.stage || null,
      description: s.description || null,
      daysLate: s.daysLate || 0,
      attachedTo: s.attachedTo || null,
    },
  };
}

/**
 * Apply the scenario's edits to its frozen copy.
 *
 * Three kinds, because three things get asked of a scenario: take this out,
 * make it a different size or land on a different day, and what if this
 * happened as well.
 */
export function applyAdjustments(base = [], adjustments = []) {
  const dropped = new Set();
  const edits = new Map();
  const added = [];

  for (const a of adjustments) {
    if (a.op === "drop") dropped.add(a.key);
    else if (a.op === "edit") edits.set(a.key, a);
    else if (a.op === "add") added.push(a);
  }

  const kept = base
    .filter((t) => !dropped.has(t.key))
    .map((t) => {
      const e = edits.get(t.key);
      if (!e) return t;
      const amount = e.amount != null ? Number(e.amount) : t.amount;
      return {
        ...t,
        amount,
        date: e.date || t.date,
        net: t.net != null && t.amount ? (t.net * amount) / t.amount : t.net,
        edited: true,
        wasAmount: t.amount,
        wasDate: t.date,
      };
    });

  const invented = added.map((a, i) => ({
    key: `scenario-add-${a.id || i}`,
    date: a.date,
    amount: Number(a.amount) || 0,
    label: a.label || "Added in this scenario",
    category: a.category || (Number(a.amount) >= 0 ? "income" : "other"),
    isPipeline: false,
    isAwaitingPo: false,
    isReceived: false,
    isHistory: false,
    isManual: true,
    // Nothing invented for a scenario is committed. That is what makes it a
    // scenario.
    committed: false,
    net: null,
    speculative: true,
    note: a.note || null,
    source: { type: "scenario", id: String(a.id || i), description: a.note || null },
  }));

  return [...kept, ...invented].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Bucket frozen transactions into the weeks a scenario covers. */
export function scenarioWeeks(txns, { fromDate, weeks, openingBalance }) {
  const start = parseDate(fromDate);
  if (!start) return [];
  const mondayOf = (d) => {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    return x;
  };
  const first = mondayOf(start);
  const out = [];
  let balance = Number(openingBalance) || 0;
  for (let i = 0; i < weeks; i++) {
    const s = new Date(first);
    s.setDate(s.getDate() + i * 7);
    const e = new Date(s);
    e.setDate(e.getDate() + 6);
    const from = isoDate(s);
    const to = isoDate(e);
    const inWeek = txns.filter((t) => t.date >= from && t.date <= to && !t.isHistory);
    const moneyIn = inWeek.filter((t) => t.amount > 0).reduce((x, t) => x + t.amount, 0);
    const moneyOut = inWeek.filter((t) => t.amount < 0).reduce((x, t) => x + t.amount, 0);
    const startBal = balance;
    balance = Math.round((balance + moneyIn + moneyOut) * 100) / 100;
    out.push({
      start: s,
      end: e,
      isHistory: false,
      txns: inWeek,
      in: moneyIn,
      out: moneyOut,
      startBal,
      endBal: balance,
    });
  }
  return out;
}

/** What this scenario does to the bottom line, against the forecast it copied. */
export function scenarioEffect(base, adjusted, opts) {
  const a = scenarioWeeks(base, opts);
  const b = scenarioWeeks(adjusted, opts);
  const low = (ws) => ws.reduce((m, w) => (m == null || w.endBal < m ? w.endBal : m), null);
  const last = (ws) => (ws.length ? ws[ws.length - 1].endBal : 0);
  return {
    lowestBefore: low(a),
    lowestAfter: low(b),
    endBefore: last(a),
    endAfter: last(b),
    inBefore: a.reduce((s, w) => s + w.in, 0),
    inAfter: b.reduce((s, w) => s + w.in, 0),
    outBefore: a.reduce((s, w) => s + w.out, 0),
    outAfter: b.reduce((s, w) => s + w.out, 0),
  };
}
